import { DOCUMENT } from '@angular/common';
import { inject, Injectable } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs';
import { environment } from '../../environments/environment';

@Injectable({ providedIn: 'root' })
export class GtmService {
  private readonly document = inject(DOCUMENT);
  private readonly router = inject(Router);
  private scriptLoaded = false;

  /**
   * Called at app startup.
   * Loads GTM only when analytics consent was previously granted.
   */
  init(analyticsConsented: boolean): void {
    if (analyticsConsented) {
      this.loadGtm();
    }
    this.trackNavigationEvents();
  }

  /**
   * Called whenever the user changes their cookie consent.
   * Loads GTM on first grant; always pushes a consent_update event.
   */
  onConsentChange(analyticsGranted: boolean): void {
    if (analyticsGranted) {
      this.loadGtm();
    }
    this.pushConsent(analyticsGranted);
  }

  /**
   * Injects the GTM script and noscript tags into the document.
   * No-ops if GTM is already loaded or if no container ID is configured.
   */
  private loadGtm(): void {
    if (this.scriptLoaded) return;
    const id = environment.googleTagManagerId;
    if (!id) return;
    this.injectScript(id);
    this.injectNoscript(id);
    this.scriptLoaded = true;
  }

  /**
   * Subscribes to Angular router NavigationEnd events and pushes a
   * `page_view` dataLayer event for each SPA route transition.
   * Called once from `init()` regardless of consent, so navigation
   * history is captured as soon as GTM loads.
   */
  private trackNavigationEvents(): void {
    this.router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe((e) => {
        const win = this.document.defaultView as (Window & { dataLayer?: unknown[] }) | null;
        if (!win) return;
        win.dataLayer ??= [];
        win.dataLayer.push({
          event: 'page_view',
          page_path: e.urlAfterRedirects,
          page_location: win.location.href,
          page_title: this.document.title,
        });
      });
  }

  /**
   * Pushes a `consent_update` event to the dataLayer so GTM can
   * react to the user's latest analytics consent choice.
   */
  private pushConsent(granted: boolean): void {
    const win = this.document.defaultView as (Window & { dataLayer?: unknown[] }) | null;
    if (!win) return;
    win.dataLayer ??= [];
    win.dataLayer.push({
      event: 'consent_update',
      analytics_storage: granted ? 'granted' : 'denied',
    });
  }

  /**
   * Appends the GTM inline bootstrap `<script>` to `<head>`.
   * The script asynchronously loads the GTM container identified by `id`.
   */
  private injectScript(id: string): void {
    const script = this.document.createElement('script');
    script.textContent = `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
})(window,document,'script','dataLayer','${id}');`;
    this.document.head.insertBefore(script, this.document.head.firstChild);
  }

  /**
   * Appends a `<noscript>` fallback iframe to `<body>` for environments
   * where JavaScript is disabled.
   */
  private injectNoscript(id: string): void {
    const noscript = this.document.createElement('noscript');
    const iframe = this.document.createElement('iframe');
    iframe.src = `https://www.googletagmanager.com/ns.html?id=${id}`;
    iframe.height = '0';
    iframe.width = '0';
    iframe.style.cssText = 'display:none;visibility:hidden';
    noscript.appendChild(iframe);
    this.document.body.insertBefore(noscript, this.document.body.firstChild);
  }
}
